import type { Cents, } from './money.js';
import type { PackageCode, SurfaceCode, Uom } from './pricing.js';

export interface Measurement {
  surfaceCode: SurfaceCode;
  /**
   * The individual entries a bidder typed, kept as scratch work: three roofline
   * segments of 50/40/10 rather than a bare 100.
   *
   * Never priced directly — the sum in linearFeet (or strandCount, on accent
   * surfaces) is what the engine charges for. That is deliberate: it means one
   * measurement per surface still holds, so the duplicate-surface rule and the
   * bid_measurements (bid_id, surface_code) primary key are untouched by
   * repeatable entry.
   */
  segments?: number[] | undefined;
  /** Set on footage-bearing surfaces (rooflines). The sum of segments. */
  linearFeet?: number | undefined;
  /**
   * Set on accent surfaces: how many of the feature there are (3 bushes, 2
   * columns) — i.e. how many accent groups were entered. Does not affect price;
   * it is the input the deferred strand auto-derivation will read.
   */
  unitCount?: number | undefined;
  /** Set on accent surfaces: strands allocated. Billed per strand. */
  strandCount?: number | undefined;
}

/**
 * A manual correction to one package's total, for something the rate card cannot
 * express — a difficult roof, a referral discount, a goodwill credit.
 *
 * Deliberately requires a label. The whole point of this tool is consistent bids,
 * so an unexplained override would quietly undo that; every adjustment carries a
 * reason, and admins can see which bids were adjusted and why.
 *
 * Scoped to one package, because the customer now receives all three prices and
 * has not chosen yet. A flat dollar discount means something very different on
 * Essential than on Showcase, so "which tier was adjusted" has to be recorded
 * rather than inferred.
 *
 * `amountCents` is signed: negative reduces the bid. It lives in BidInput, so it
 * is captured in the bid's stored input and replays exactly on recompute.
 */
export interface Adjustment {
  /** Which package this adjustment applies to. */
  packageCode: PackageCode;
  /** Why. Required, and shown to admins — never blank. */
  label: string;
  /** Signed whole cents. Negative is a reduction. */
  amountCents: Cents;
}

/**
 * How many of a consumable this tier ADDS, entered by the bidder because nothing
 * can derive it.
 *
 * Additive, exactly like roofline footage: a tier's effective count is its own
 * plus every lower tier's. A higher tier really does use more plugs, so the three
 * margin figures have to diverge rather than share one flat count.
 *
 * Internal only. These never change what a customer pays — they feed the cost and
 * margin figures, which exclude labour.
 */
export interface EngineeringCount {
  /** The tier this count is entered under. */
  packageCode: PackageCode;
  /** An inventory item's SKU. */
  sku: string;
  /** How many this tier adds. Whole units; feet for the per-foot items. */
  qty: number;
}

export interface BidInput {
  colorCode: string;
  clipColorCode: string;
  /**
   * One entry per measured surface. Which tiers a surface is priced into comes
   * from the pricing config's package/surface join, not from anything here, so a
   * bidder measures once and all three packages are quoted from it.
   */
  measurements: Measurement[];
  travelMiles: number;
  /** Shown on each package's customer-facing line, e.g. "2026 Season". */
  seasonLabel: string;
  /** Manual corrections, applied after travel, scoped per package. */
  adjustments?: Adjustment[] | undefined;
  /** Internal consumable counts. Never affect price. */
  engineering?: EngineeringCount[] | undefined;
}

export type LineUom = Uom | 'mile' | 'strand' | 'bid' | 'adjustment';

export interface BidLine {
  sort: number;
  description: string;
  qty: number;
  uom: LineUom;
  unitPriceCents: Cents;
  amountCents: Cents;
  /**
   * The customer sees one all-in season price per package. Internal lines carry
   * the per-surface breakdown for admin review and reporting.
   */
  isCustomerVisible: boolean;
}

export interface MaterialLine {
  sku: string;
  name: string;
  qty: number;
  uom: Uom;
  unitCostCents: Cents;
  extendedCostCents: Cents;
}

/**
 * One package's price, computed from the measurements that package includes.
 *
 * Self-contained on purpose: subtotal + travel + adjustments equals totalCents
 * within a single quote, so a displayed tier always adds up without reaching for
 * anything outside it.
 */
export interface PackageQuote {
  packageCode: PackageCode;
  /** This tier's effective per-foot rate, including any colour delta. */
  ratePerFootCents: Cents;

  /** Cumulative through this tier: Signature's footage includes Essential's. */
  totalLinearFeet: number;
  totalStrands: number;
  runCount: number;

  /** This tier's internal per-surface breakdown. */
  lines: BidLine[];

  /** Lighting charge before travel. */
  subtotalCents: Cents;
  /** The same drive whichever package is chosen, so identical across quotes. */
  travelFeeCents: Cents;
  billableTravelMiles: number;
  /** Net of this package's manual adjustments; signed. Zero when there were none. */
  adjustmentTotalCents: Cents;
  totalCents: Cents;

  /**
   * Internal only. Costs are never billed to the customer — the per-foot rate
   * covers materials. This exists for margin tracking and pricing analysis.
   *
   * Named "material" for historical reasons, but the cost list is open-ended: an
   * admin can add non-material inputs such as customer acquisition. Whatever is
   * in it, LABOUR IS NOT MODELLED, so this is not profit.
   */
  materials: MaterialLine[];
  estMaterialCostCents: Cents;
  materialsMarginCents: Cents;
  /** Cost margin as a percentage of this tier's total. EXCLUDES LABOUR. */
  materialsMarginPct: number;
}

export interface BidResult {
  /**
   * The build of the calc engine that produced this result. Stamped alongside
   * pricingVersionId because an offline PWA can run an older engine build than
   * the server: same source, different bundle.
   */
  engineVersion: string;
  pricingVersionId: string;

  /**
   * One quote per package, in the config's package sort order. The customer
   * receives all of these and chooses afterwards.
   *
   * This is the only place the internal breakdown lives. There is deliberately no
   * top-level `lines`: concatenating all three tiers would repeat the shared
   * roofline line three times, and anything summing over it would treble-count.
   */
  quotes: PackageQuote[];

  /**
   * One all-in line per package — what the customer actually sees.
   *
   * Kept flat and separate from `quotes` because this is the array the delivery
   * projection whitelists from. An external system is handed these and nothing
   * else, so a new field on PackageQuote cannot start leaking by default.
   */
  customerLines: BidLine[];

  /** Non-fatal notes for the estimator or admin (e.g. unpriced inventory). */
  warnings: string[];
}

/** Thrown for input the engine refuses to price at all. */
export class BidValidationError extends Error {
  readonly issues: string[];
  constructor(issues: string[]) {
    super(`Bid input is not valid: ${issues.join('; ')}`);
    this.name = 'BidValidationError';
    this.issues = issues;
  }
}
